import React from "react";
import { BRAND, FEATURES, HERO_IMAGE } from "../mock";
import Logo from "../components/Logo";
import { ShieldCheck, FlaskConical, Truck, Award } from "lucide-react";
import SEO from "../components/SEO";

const About = () => {
  return (
    <div>
      <SEO
        title="About — A Chemical Supplier of Research Compounds"
        description="Certified Wellness is a U.S.-based chemical supplier of HPLC-tested research peptides and blends. Made in the USA with COA on every batch."
        canonical="/about"
      />
      {/* Hero */}
      <section className="hero-clinical relative overflow-hidden border-b border-gray-100">
        <div className="hero-grid-bg absolute inset-0 pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-4 lg:px-10 py-24 relative z-10">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-7">
              <div className="h-px w-10 bg-[#b91c1c]" />
              <span className="text-[10px] font-semibold tracking-[0.32em] uppercase text-[#b91c1c]">
                About {BRAND.name}
              </span>
            </div>
            <h1 className="font-display text-[48px] md:text-[64px] lg:text-[78px] font-semibold leading-[1.02] text-[#0a0a0a] tracking-tight">
              Clinical-grade compounds.<br />
              <span className="font-serif italic font-normal text-[#b91c1c]">Zero compromise.</span>
            </h1>
            <p className="text-[18px] text-gray-500 mt-8 max-w-2xl leading-[1.7] font-light">
              {BRAND.name} was founded on a single principle—research-grade compounds should be
              accessible, transparent, and independently verified. Every batch is third-party
              HPLC-tested. Every order ships with a Certificate of Analysis on file.
            </p>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="max-w-[1400px] mx-auto px-4 lg:px-10 py-24 grid grid-cols-1 md:grid-cols-[5fr_7fr] gap-16 items-center">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-8 bg-[#b91c1c]" />
            <span className="text-[10px] font-semibold tracking-[0.32em] uppercase text-[#b91c1c]">Our Mission</span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0a0a0a] tracking-tight leading-[1.05]">
            Precision, purity,<br />
            <span className="font-serif italic font-normal">trust.</span>
          </h2>
          <p className="text-gray-500 leading-[1.7] font-light mt-6">
            We exist to power serious research with compounds you can actually trust. From
            clinicians and lab researchers to performance professionals, our customers
            expect the highest standard of purity—and we deliver it on every batch,
            without exception.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-px bg-gray-200">
          {[
            { k: "99%+", l: "Verified Purity" },
            { k: "HPLC", l: "3rd-Party Tested" },
            { k: "USA", l: "FDA-Reg Facility" },
            { k: "24h", l: "Order Dispatch" },
            { k: "COA", l: "On Every Batch" },
            { k: "21+", l: "Research Use Only" },
          ].map((item) => (
            <div key={item.l} className="bg-white p-8">
              <div className="font-display text-3xl font-semibold text-[#0a0a0a]">{item.k}</div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mt-2">{item.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Process */}
      <section className="bg-[#fafbfc] border-y border-gray-100">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-10 py-24">
          <div className="max-w-2xl mb-16">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-8 bg-[#b91c1c]" />
              <span className="text-[10px] font-semibold tracking-[0.32em] uppercase text-[#b91c1c]">The Process</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0a0a0a] tracking-tight leading-[1.05]">
              From lab to your <span className="font-serif italic font-normal">doorstep.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-gray-200">
            {[
              { n: "01", t: "Source", d: "Premium-grade raw materials sourced from vetted American suppliers." },
              { n: "02", t: "Synthesize", d: "Manufactured in FDA-registered, GMP-aligned American facilities." },
              { n: "03", t: "Test", d: "Independent third-party HPLC verification on every single batch." },
              { n: "04", t: "Ship", d: "Discreet, temperature-controlled, fast tracked delivery worldwide." },
            ].map((s) => (
              <div key={s.n} className="bg-white p-8 hover:bg-[#fafbfc] transition-colors">
                <div className="text-[11px] tracking-[0.3em] text-[#b91c1c] font-semibold mb-3">{s.n}</div>
                <div className="font-display text-xl font-semibold text-[#0a0a0a]">{s.t}</div>
                <p className="text-gray-500 text-sm mt-3 font-light leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="max-w-[1400px] mx-auto px-4 lg:px-10 py-16 grid grid-cols-2 md:grid-cols-4 gap-10">
        {[
          { icon: ShieldCheck, t: FEATURES[0].title, d: FEATURES[0].desc },
          { icon: Award, t: FEATURES[1].title, d: FEATURES[1].desc },
          { icon: Truck, t: FEATURES[2].title, d: FEATURES[2].desc },
          { icon: FlaskConical, t: FEATURES[3].title, d: FEATURES[3].desc },
        ].map(({ icon: Icon, t, d }) => (
          <div key={t}>
            <Icon className="h-5 w-5 text-[#b91c1c] mb-3" />
            <div className="font-display font-semibold text-[#0a0a0a]">{t}</div>
            <div className="text-[13px] text-gray-500 mt-1 font-light">{d}</div>
          </div>
        ))}
      </section>
    </div>
  );
};

export default About;
